/**
 * Candidate selection: one survivor, or an honest question (plan §6.2/§6.3).
 *
 * @remarks
 * Selection never guesses (product pillar). The rules, applied in order:
 *
 *   1. **Match tier is absolute** (§6.2's `exact > alias > fuzzy` applied to
 *      entity heads): when any candidate matched a canonical noun,
 *      alias-matched candidates do not compete at all.
 *   2. **The verb's side of the room** (D134): when the intent declares an
 *      `objectPreference` and any survivor is where it points — `carried`
 *      for `drop`, `present` (loose or fixed in the room) for `take` — only
 *      those compete. A verb with no preference reaches for nothing, so a
 *      noun answering on both sides of the room becomes a question rather
 *      than a rank: the rule the player can learn is "the verb decides;
 *      when the verb does not care, you are asked."
 *   3. **The universal tie-break** orders the surviving pool: score
 *      descending → salience descending → lexicographic on ref. Deterministic
 *      everywhere, byte for byte.
 *   4. **Closeness is ambiguity**: survivors within {@link CLARIFICATION_SCORE_WINDOW}
 *      of the top score become a clarification (capped at
 *      {@link CLARIFICATION_CANDIDATE_CAP}), never a silent pick.
 *
 * @packageDocumentation
 */

import { type ObjectPreference } from '../lexicon/lexicon-definition.js';
import { type EntityLocation, type ParseCandidateEntity } from '../types.js';

/** How many candidates a clarification carries at most (plan §2.1). */
export const CLARIFICATION_CANDIDATE_CAP = 4;

/** Survivors within this score distance of the top are ambiguous — ask, never guess (plan §6.3). */
export const CLARIFICATION_SCORE_WINDOW = 0.15;

/** How a candidate's head matched: canonical noun, or alias (noun beats alias absolutely — §6.2). */
export type EntityMatchKind = 'alias' | 'noun';

/** The outcome of selecting among one phrase's surviving candidates. */
export type EntitySelectionOutcome =
	| { readonly contenders: readonly ScoredEntityCandidate[]; readonly type: 'needs_clarification' }
	| { readonly selected: ScoredEntityCandidate; readonly type: 'selected' };

/** One scored survivor of entity-phrase matching. */
export interface ScoredEntityCandidate {
	readonly entity: ParseCandidateEntity;
	readonly matchKind: EntityMatchKind;
	/** The catalog noun/alias text the phrase head matched. */
	readonly matchedName: string;
	/** Adjectives consumed while this candidate survived filtering, in phrase order. */
	readonly qualifiers: readonly string[];
	/** Selection weight — salience in M4; fuzzy corrections adjust it in M5. */
	readonly score: number;
}

/** The locations a preference reaches for (D134). */
const LOCATIONS_BY_PREFERENCE: Readonly<Record<Exclude<ObjectPreference, 'none'>, readonly EntityLocation[]>> = {
	carried: ['carried'],
	present: ['fixture', 'present'],
};

/**
 * Select one candidate, or report the contenders when selecting would be a guess.
 *
 * @param candidates - The phrase's surviving candidates (at least one)
 * @param objectPreference - The verb's side of the room (D134); `none` reaches for nothing
 * @returns The selection, or the ranked contenders needing clarification
 * @throws Error on an empty candidate list (programmer error — resolution reports `not_found` before this stage)
 */
export const selectOrClarify = (
	candidates: readonly ScoredEntityCandidate[],
	objectPreference: ObjectPreference = 'none',
): EntitySelectionOutcome => {
	// Tier is absolute: alias-matched candidates only compete when no
	// canonical-noun match exists (§6.2 applied to entity heads).
	const nounTierCandidates = candidates.filter((candidate) => candidate.matchKind === 'noun');
	const matchTierPool = nounTierCandidates.length > 0 ? nounTierCandidates : candidates;
	const competingPool = preferredPool(matchTierPool, objectPreference);
	const rankedCandidates = [...competingPool].sort(compareScoredCandidates);
	const [topCandidate] = rankedCandidates;

	if (topCandidate === undefined) {
		throw new Error('selectOrClarify requires at least one candidate; resolution reports not_found before this stage');
	}

	const contenders = rankedCandidates.filter(
		(candidate) => topCandidate.score - candidate.score <= CLARIFICATION_SCORE_WINDOW,
	);

	if (contenders.length === 1) {
		return { selected: topCandidate, type: 'selected' };
	}

	return { contenders: contenders.slice(0, CLARIFICATION_CANDIDATE_CAP), type: 'needs_clarification' };
};

/**
 * D134: the candidates where the verb reaches, when any are there — else
 * everyone, because a preference is a preference and not a filter that can
 * leave the player with nothing.
 *
 * @param pool - The match-tier survivors
 * @param objectPreference - The verb's side of the room
 * @returns The candidates that compete
 */
const preferredPool = (
	pool: readonly ScoredEntityCandidate[],
	objectPreference: ObjectPreference,
): readonly ScoredEntityCandidate[] => {
	if (objectPreference === 'none') {
		return pool;
	}

	const reachedLocations = LOCATIONS_BY_PREFERENCE[objectPreference];
	const reached = pool.filter(
		(candidate) => candidate.entity.location !== undefined && reachedLocations.includes(candidate.entity.location),
	);

	return reached.length > 0 ? reached : pool;
};

/**
 * The universal deterministic ordering (plan §6.2): score descending →
 * salience descending → lexicographic on entity ref.
 *
 * @param leftCandidate - One candidate
 * @param rightCandidate - The other candidate
 * @returns A standard comparator result
 */
const compareScoredCandidates = (leftCandidate: ScoredEntityCandidate, rightCandidate: ScoredEntityCandidate): number =>
	rightCandidate.score - leftCandidate.score ||
	rightCandidate.entity.salience - leftCandidate.entity.salience ||
	leftCandidate.entity.entityRef.localeCompare(rightCandidate.entity.entityRef, 'en');
